import type { z } from "zod";

interface DraftMachineRef {
  tmpId: string;
  basic: { code: string };
  parameters: { tmpId: string }[];
  sensorLinks: { tmpId: string }[];
  compatibilities: { tmpId: string }[];
}

interface DraftCanvasNodeRef {
  tmpId: string;
  type: string;
  machineTmpId?: string | null;
}

interface DraftCanvasEdgeRef {
  tmpId: string;
  sourceNodeTmpId: string;
  targetNodeTmpId: string;
}

interface AtomicRequestData {
  machines: DraftMachineRef[];
  canvas: { nodes: DraftCanvasNodeRef[]; edges: DraftCanvasEdgeRef[] };
}

interface Limits {
  parameters: number;
  sensorLinks: number;
  compatibilities: number;
}

export function checkAtomicLimits(
  data: AtomicRequestData,
  ctx: z.RefinementCtx,
  limits: Limits,
  zod: typeof z
): void {
  const totalParams = data.machines.reduce((acc, m) => acc + m.parameters.length, 0);
  if (totalParams > limits.parameters) {
    ctx.addIssue({
      code: zod.ZodIssueCode.too_big,
      type: "array",
      maximum: limits.parameters,
      inclusive: true,
      message: `Máximo de ${limits.parameters} parâmetros somados`,
      path: ["machines"],
    });
  }
  const totalSensors = data.machines.reduce((acc, m) => acc + m.sensorLinks.length, 0);
  if (totalSensors > limits.sensorLinks) {
    ctx.addIssue({
      code: zod.ZodIssueCode.too_big,
      type: "array",
      maximum: limits.sensorLinks,
      inclusive: true,
      message: `Máximo de ${limits.sensorLinks} sensores vinculados`,
      path: ["machines"],
    });
  }
  const totalCompat = data.machines.reduce((acc, m) => acc + m.compatibilities.length, 0);
  if (totalCompat > limits.compatibilities) {
    ctx.addIssue({
      code: zod.ZodIssueCode.too_big,
      type: "array",
      maximum: limits.compatibilities,
      inclusive: true,
      message: `Máximo de ${limits.compatibilities} compatibilidades`,
      path: ["machines"],
    });
  }
}

export function checkAtomicCanvas(
  data: AtomicRequestData,
  ctx: z.RefinementCtx,
  zod: typeof z
): void {
  const machineIds = new Set(data.machines.map((m) => m.tmpId));
  const nodeIds = new Set(data.canvas.nodes.map((n) => n.tmpId));

  for (const node of data.canvas.nodes) {
    if (node.type === "MACHINE") {
      if (!node.machineTmpId) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: `Nó MACHINE ${node.tmpId} sem machineTmpId.`,
          path: ["canvas", "nodes"],
        });
      } else if (!machineIds.has(node.machineTmpId)) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: `Nó ${node.tmpId} aponta para machineTmpId inexistente: ${node.machineTmpId}.`,
          path: ["canvas", "nodes"],
        });
      }
    }
  }
  for (const edge of data.canvas.edges) {
    if (!nodeIds.has(edge.sourceNodeTmpId)) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: `Conexão ${edge.tmpId} referencia nó de origem inexistente: ${edge.sourceNodeTmpId}.`,
        path: ["canvas", "edges"],
      });
    }
    if (!nodeIds.has(edge.targetNodeTmpId)) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: `Conexão ${edge.tmpId} referencia nó de destino inexistente: ${edge.targetNodeTmpId}.`,
        path: ["canvas", "edges"],
      });
    }
  }

  const starts = data.canvas.nodes.filter((n) => n.type === "START").length;
  const ends = data.canvas.nodes.filter((n) => n.type === "END").length;
  if (starts !== 1) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      message: `Canvas deve conter exatamente 1 nó de início (encontrado: ${starts}).`,
      path: ["canvas", "nodes"],
    });
  }
  if (ends !== 1) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      message: `Canvas deve conter exatamente 1 nó de fim (encontrado: ${ends}).`,
      path: ["canvas", "nodes"],
    });
  }
}

export function checkAtomicMachineCodes(
  data: AtomicRequestData,
  ctx: z.RefinementCtx,
  zod: typeof z
): void {
  const codes = data.machines.map((m) => m.basic.code);
  if (new Set(codes).size !== codes.length) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      message: "Códigos de máquina duplicados no payload.",
      path: ["machines"],
    });
  }
}
